import type { FormInstance } from 'antd';
import { Form, message, Select } from 'antd';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import {
  applyPick,
  type CompanyPick,
  choiceOf,
  companyOptionValue,
  optionValueOf,
  type PickerCompany,
  type PickerSponsor,
  type PickFields,
  sponsorOptionValue,
} from '../utils/newTicketCompanyPicker';

const FILLED_FIELDS = ['organizationName', 'attendeeType'] as const;

/** Takes back the current pick, undoing what it filled in. */
export function clearCompanyPick(form: FormInstance): void {
  const { fields } = applyPick(
    form.getFieldsValue([...FILLED_FIELDS]) as PickFields,
    form.getFieldValue('companyPick'),
    undefined
  );
  form.setFieldsValue({ ...fields, companyPick: undefined });
}

interface PickerEvent {
  id: string;
  organizationId: string;
}

interface NewTicketCompanyPickerProps {
  event: PickerEvent | undefined;
  id?: string;
  value?: CompanyPick;
  onChange?: (pick: CompanyPick | undefined) => void;
}

interface PickerOptions {
  companies: PickerCompany[];
  sponsors: PickerSponsor[];
}

const NO_OPTIONS: PickerOptions = { companies: [], sponsors: [] };

// Picking a sponsor never sets `sponsorId`: that would make the ticket the
// sponsor's delegate.
export function NewTicketCompanyPicker({
  event,
  id,
  value,
  onChange,
}: NewTicketCompanyPickerProps): JSX.Element {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const [options, setOptions] = useState<PickerOptions>(NO_OPTIONS);
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `attempt` is the retry trigger.
  useEffect(() => {
    setOptions(NO_OPTIONS);
    setFailed(false);
    if (!event) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      // Without organizationId a SUPER_ADMIN would get every tenant's companies.
      api.get<PickerCompany[]>('/companies', {
        params: { activeEventsOnly: 'false', organizationId: event.organizationId },
      }),
      api.get<PickerSponsor[]>(`/events/${event.id}/sponsors/admin`),
    ])
      .then(([companies, sponsors]) => {
        if (cancelled) return;
        setOptions({
          companies: companies.data.map(({ id, name }) => ({ id, name })),
          sponsors: sponsors.data.map(({ id, name, companyId }) => ({ id, name, companyId })),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setFailed(true);
        message.error(t('tickets.pickerLoadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [event, attempt, t]);

  const handleChange = (optionValue: string | undefined) => {
    const { fields, pick } = applyPick(
      form.getFieldsValue([...FILLED_FIELDS]) as PickFields,
      value,
      choiceOf(optionValue, options.sponsors)
    );
    form.setFieldsValue(fields);
    onChange?.(pick);
  };

  return (
    <Select
      id={id}
      placeholder={event ? t('tickets.selectCompanyPlaceholder') : t('tickets.selectEventFirst')}
      disabled={!event}
      loading={loading}
      allowClear
      showSearch
      optionFilterProp="label"
      value={optionValueOf(value)}
      onChange={handleChange}
      onOpenChange={(open) => {
        if (open && failed) setAttempt((n) => n + 1);
      }}
      options={[
        {
          label: t('tickets.pickerSponsorsGroup'),
          options: options.sponsors.map((s) => ({
            value: sponsorOptionValue(s.id),
            label: s.name,
          })),
        },
        {
          label: t('tickets.pickerCompaniesGroup'),
          options: options.companies.map((c) => ({
            value: companyOptionValue(c.id),
            label: c.name,
          })),
        },
      ].filter((group) => group.options.length > 0)}
    />
  );
}

export default NewTicketCompanyPicker;
